/**
 * ProjectWorkOrderCreatePage - Create or edit a Project Work Order.
 *
 * Despite the file name, this page handles both modes:
 *   - /project-work-orders/create — POSTs a new project
 *   - /project-work-orders/:id/edit — PATCHes an existing project
 *
 * Captures the fields needed to stand up a project (company, building,
 * name, description, business line, total hour pool, optional schedule).
 * Child work orders are attached from the detail page after creation —
 * that route reuses the existing WO create form, which already handles
 * the full set of WO fields (location, assignees, billing, etc.).
 * Splitting the flow this way keeps the project form short and leans on
 * the WO form we already maintain.
 *
 * The Project Information block captured here (name, description,
 * business line) is what the child WO create form pre-fills from, so
 * whatever is entered on this page carries straight over to every work
 * order added under the project.
 *
 * @route /project-work-orders/create
 * @route /project-work-orders/:id/edit
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, camelizeKeys } from '../../services/api';
import { projectWorkOrderService } from '../../services/projectWorkOrderService';
import { businessLineService } from '../../services/businessLineService';
import type { BusinessLine } from '../../services/businessLineService';
import type { ProjectWorkOrderStatus } from '../../services/projectWorkOrderService';
import { qk } from '../../lib/queryKeys';
import { Button, Input, BackLink, Alert } from '../../components/atoms';
import { AsyncSearchableSelect } from '../../components/molecules/AsyncSearchableSelect';
import type { AsyncSearchableSelectOption } from '../../components/molecules/AsyncSearchableSelect';
import { PageContainer } from '../../components/templates/PageContainer';
import { BILLING_INFO_TAGS } from '../../utils/constants';

interface CompanyOption { id: number; name: string }
interface LocationOption {
	id: number;
	name: string;
	/** Composed "address, city, state ZIP" for the secondary display line. */
	addressSummary: string;
}

/** Build the display label used in both the dropdown and the closed-input
 *  state. Centralized so the same string shows up in both places. */
const locationOptionLabel = (l: LocationOption | undefined): string => {
	if (!l) return '';
	return l.addressSummary ? `${l.name} — ${l.addressSummary}` : l.name;
};

/** Billing classification tags shown as toggleable chips. Sourced from the
 *  shared BILLING_INFO_TAGS list so this form and JobForm label the same
 *  underlying values identically. */
const BILLING_INFO_OPTIONS: string[] = [...BILLING_INFO_TAGS];

/** Recurring frequency options. Mirrors JobForm's FREQUENCY_OPTIONS. */
const FREQUENCY_OPTIONS = [
	'One-Time',
	'Daily',
	'Weekly',
	'Bi-Weekly',
	'Monthly',
	'Quarterly',
	'Semi-Annually',
	'Annually',
	'As Needed',
];

const ProjectWorkOrderCreatePage: React.FC = () => {
	const navigate = useNavigate();
	/** Edit mode is driven by the presence of `:id` in the route. The
	 *  /create route doesn't have one; /:id/edit does. */
	const { id: routeId } = useParams<{ id: string }>();
	const isEditMode = Boolean(routeId);
	/* Duplicate mode is driven by ?duplicate=<sourceId> on the /create
	 * route. We fetch the source project and prefill every field except
	 * status (forced back to draft) and the start/end dates (cleared) —
	 * mirrors the WO list and Inspection list "Duplicate" flow so the
	 * user has a fresh project that doesn't inherit a stale schedule. */
	const [searchParams] = useSearchParams();
	const duplicateId = searchParams.get('duplicate');
	const isDuplicateMode = !isEditMode && Boolean(duplicateId);

	/* --- Form state --- */
	const [companyId, setCompanyId] = useState<string>('');
	const [locationId, setLocationId] = useState<string>('');
	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	/* Business line for the whole project. Every child WO inherits this
	 * slug on its create form, so a project's work orders can't drift
	 * onto different lines. Stored as the CMS slug, not the label. */
	const [businessLine, setBusinessLine] = useState('');
	const [totalHours, setTotalHours] = useState<string>('');
	const [status, setStatus] = useState<ProjectWorkOrderStatus>('draft');
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [notes, setNotes] = useState('');
	/* Billing + Settings — these were moved up from the child WO form
	 * so they apply uniformly across all children in this project. */
	const [billingInfo, setBillingInfo] = useState<string[]>([]);
	const [billingNotes, setBillingNotes] = useState('');
	const [frequency, setFrequency] = useState('');

	const queryClient = useQueryClient();

	/** Locations associated with the selected company. Loaded after the
	 *  user picks a company so the building list is always scoped. */
	const [locations, setLocations] = useState<LocationOption[]>([]);
	const [isLoadingLocations, setIsLoadingLocations] = useState(false);
	/** Display label for the prefilled building (from the project's eager-
	 *  loaded location relation). Lets the closed picker show "Carmel
	 *  Medical Plaza — 200 Medical Drive, …" immediately in edit/duplicate
	 *  mode instead of the raw id while the company's location list is
	 *  still loading. */
	const [prefillLocationLabel, setPrefillLocationLabel] = useState('');

	/* --- UI state --- */
	const [error, setError] = useState<string | null>(null);
	/** Initial-fetch indicator for the two prefill modes (edit, duplicate).
	 *  Hides the form behind a spinner so the user doesn't briefly see an
	 *  empty form before the prefill lands. */
	const [isLoadingEdit, setIsLoadingEdit] = useState<boolean>(isEditMode || isDuplicateMode);

	/* Load the company list once on mount. Mirror the dedup pattern the CMS
	 * editor uses so re-seeded environments with duplicate rows don't pollute
	 * the picker. The queryFn returns the deduped, sorted CompanyOption[]; a
	 * fetch failure is non-fatal (the user can still navigate away, and the
	 * submit step surfaces a clearer error if companyId is empty). */
	const companiesQuery = useQuery({
		queryKey: qk.companies.list({ per_page: '999' }),
		queryFn: async (): Promise<CompanyOption[]> => {
			const res = await api.get<any>('/companies', { per_page: '999' });
			const raw = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
			const rows = camelizeKeys(raw) as Record<string, unknown>[];
			/* Dedupe by case-insensitive trimmed name rather than id.
			 * Some environments have multiple companies rows sharing a
			 * name (e.g. seeders that were re-run without truncating),
			 * each with its own id — id-based dedup lets them through
			 * and the picker shows duplicates. Keep the lowest-id row
			 * as canonical so the picker always selects the oldest
			 * record for a given name. */
			const map = new Map<string, CompanyOption>();
			for (const c of rows as any[]) {
				const id = Number(c.companyId ?? c.id);
				const name = typeof c.name === 'string' ? c.name : '';
				if (!Number.isFinite(id) || !name.trim()) continue;
				const key = name.trim().toLowerCase();
				const existing = map.get(key);
				if (!existing || id < existing.id) {
					map.set(key, { id, name });
				}
			}
			return Array.from(map.values()).sort((a, b) =>
				a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
			);
		},
	});
	const companies: CompanyOption[] = useMemo(() => companiesQuery.data ?? [], [companiesQuery.data]);

	/* CMS-managed business lines, active rows only — the same list the
	 * WO / Inspection forms pick from (see ProjectInformationSection).
	 * A fetch failure is non-fatal: the select falls back to its empty
	 * placeholder and the field stays optional. */
	const businessLinesQuery = useQuery({
		queryKey: qk.businessLines.list({ active: '1' }),
		queryFn: async (): Promise<BusinessLine[]> => {
			const res = await businessLineService.list(true);
			return (res.data as any)?.data ?? [];
		},
	});
	const businessLines: BusinessLine[] = useMemo(() => businessLinesQuery.data ?? [], [businessLinesQuery.data]);

	/* Prefill effect — covers both edit mode (load by :id from the route)
	 * and duplicate mode (load by ?duplicate=<id> from the query string).
	 *
	 * Edit:      copy every field verbatim so the user can amend in place.
	 * Duplicate: copy everything except identity (no id), status (forced
	 *            back to 'draft' so the duplicate doesn't inherit
	 *            'completed'/'cancelled' state), and start/end dates
	 *            (cleared — the duplicate is for a new schedule window).
	 *            Also append " (Copy)" to the name so the user can
	 *            distinguish the two records at a glance.
	 *
	 * Date columns arrive as ISO strings — strip the time portion so HTML
	 * date inputs accept them. Setting companyId here also triggers the
	 * buildings effect below, which loads the per-company location list
	 * so the picker can display the prefilled building's label correctly. */
	/* Fetch the source project (edit: routeId; duplicate: duplicateId) and
	 * camelize. The effect below consumes the result to populate the form. */
	const sourceId = routeId || duplicateId;
	const prefillQuery = useQuery({
		queryKey: qk.projectWorkOrders.detail(sourceId ?? ''),
		queryFn: async (): Promise<Record<string, any>> => {
			const res = await projectWorkOrderService.getById(sourceId as string);
			const raw = (res.data as any)?.data ?? res.data;
			return camelizeKeys(raw) as Record<string, any>;
		},
		enabled: !!sourceId,
	});

	useEffect(() => {
		if (!sourceId) return;
		if (prefillQuery.isError) {
			setError(prefillQuery.error instanceof Error ? prefillQuery.error.message : (isDuplicateMode ? 'Failed to load source project to duplicate' : 'Failed to load project for editing'));
			setIsLoadingEdit(false);
			return;
		}
		const proj = prefillQuery.data;
		if (!proj) return;
		setCompanyId(proj.companyId != null ? String(proj.companyId) : '');
		setLocationId(proj.locationId != null ? String(proj.locationId) : '');
		/* Remember the building's label from the eager-loaded relation so
		 * the picker can render it before (and independent of) the
		 * per-company locations fetch below. */
		const projLocation = proj.location as Record<string, unknown> | null | undefined;
		if (projLocation && typeof projLocation.name === 'string') {
			const summary = [projLocation.address, projLocation.city, projLocation.state, projLocation.zip]
				.filter((v) => typeof v === 'string' && (v as string).trim() !== '')
				.join(', ');
			setPrefillLocationLabel(summary ? `${projLocation.name} — ${summary}` : projLocation.name);
		}
		setName(proj.name ? (isDuplicateMode ? `${proj.name} (Copy)` : proj.name) : '');
		setDescription(proj.description ?? '');
		setBusinessLine(proj.businessLine ?? '');
		setTotalHours(proj.totalHours != null ? String(proj.totalHours) : '');
		/* In duplicate mode reset status to draft so the new
		 * project starts in a safe, editable state regardless of
		 * the source's current lifecycle. */
		if (isDuplicateMode) {
			setStatus('draft');
		} else if (proj.status) {
			setStatus(proj.status as ProjectWorkOrderStatus);
		}
		/* Duplicate mode clears dates — the schedule on a copy
		 * almost never matches the original. */
		if (isDuplicateMode) {
			setStartDate('');
			setEndDate('');
		} else {
			setStartDate(proj.startDate ? String(proj.startDate).split('T')[0] : '');
			setEndDate(proj.endDate ? String(proj.endDate).split('T')[0] : '');
		}
		setNotes(proj.notes ?? '');
		setBillingInfo(Array.isArray(proj.billingInfo) ? proj.billingInfo as string[] : []);
		setBillingNotes(proj.billingNotes ?? '');
		setFrequency(proj.frequency ?? '');
		setIsLoadingEdit(false);
	}, [sourceId, prefillQuery.data, prefillQuery.isError, prefillQuery.error, isDuplicateMode]);

	/* When the company changes, fetch the buildings the API associates
	 * with it via the company_locations pivot. The /companies/{id}/locations
	 * route reuses LocationController@index and filters by company. */
	useEffect(() => {
		if (!companyId) {
			setLocations([]);
			/* Only clear the building when there's no prefill in play. In
			 * edit/duplicate mode the prefill query is often already cached
			 * (the detail page shares its query key), so the prefill effect
			 * above sets companyId + locationId during the same mount
			 * effect-phase as this run — whose closure still sees the
			 * initial companyId=''. Unconditionally clearing here wiped the
			 * just-prefilled building, which is why "Edit" opened with an
			 * empty Building field. */
			if (!prefillQuery.data) {
				setLocationId('');
			}
			return;
		}
		let cancelled = false;
		setIsLoadingLocations(true);
		(async () => {
			try {
				const res = await api.get<any>(`/companies/${companyId}/locations`);
				if (cancelled) return;
				const raw = Array.isArray(res.data) ? res.data : (res.data?.data ?? []);
				const rows = camelizeKeys(raw) as Record<string, unknown>[];
				const opts: LocationOption[] = (rows as any[]).map((l) => ({
					id: Number(l.id),
					name: String(l.name ?? ''),
					/* Locations table stores the postal code in a `zip` column;
					 * camelizeKeys preserves it as `zip` (no underscore to
					 * convert). Use that directly so the address summary
					 * actually shows the ZIP. */
					addressSummary: [l.address, l.city, l.state, l.zip]
						.filter((v) => typeof v === 'string' && v.trim() !== '')
						.join(', '),
				})).filter((l) => Number.isFinite(l.id) && l.name);
				setLocations(opts);
				/* Clear the selection if it no longer belongs to the
				 * newly-selected company. Functional update: this effect's
				 * deps are [companyId] only, so the closure's locationId can
				 * be stale by the time the fetch resolves — read the current
				 * value instead. */
				setLocationId((current) =>
					current && !opts.some((l) => String(l.id) === current) ? '' : current
				);
			} catch (err) {
				/* Non-fatal — leave the locations list empty so the picker
				 * shows the empty state and submission reports the missing
				 * field. */
				setLocations([]);
			} finally {
				if (!cancelled) setIsLoadingLocations(false);
			}
		})();
		return () => { cancelled = true; };
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [companyId]);

	/** Filter the already-loaded buildings list against the user's typed
	 *  query. AsyncSearchableSelect calls this on every (debounced)
	 *  keystroke; we resolve synchronously since the list is already in
	 *  memory and per-company size is small. Matches against both the
	 *  building name and the address summary so a partial street name
	 *  is enough. */
	const searchLocations = useCallback(async (search: string): Promise<AsyncSearchableSelectOption[]> => {
		const q = search.trim().toLowerCase();
		const list = q
			? locations.filter((l) =>
				l.name.toLowerCase().includes(q) || l.addressSummary.toLowerCase().includes(q))
			: locations;
		return list.map((l) => ({ value: String(l.id), label: locationOptionLabel(l) }));
	}, [locations]);

	/* On a successful create/update, invalidate the project-WO cache so the
	 * list page and (on edit) the detail page reflect the new values when the
	 * user navigates to them. */
	const updateMutation = useMutation({
		mutationFn: (vars: { id: string; payload: Parameters<typeof projectWorkOrderService.update>[1] }) =>
			projectWorkOrderService.update(vars.id, vars.payload),
		onSuccess: (_res, vars) => {
			queryClient.invalidateQueries({ queryKey: qk.projectWorkOrders.all });
			queryClient.invalidateQueries({ queryKey: qk.projectWorkOrders.detail(vars.id) });
		},
	});

	const createMutation = useMutation({
		mutationFn: (payload: Parameters<typeof projectWorkOrderService.create>[0]) =>
			projectWorkOrderService.create(payload),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: qk.projectWorkOrders.all });
		},
	});

	const isSaving = updateMutation.isPending || createMutation.isPending;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);

		const cidNum = Number(companyId);
		const lidNum = Number(locationId);
		const hoursNum = Number(totalHours);
		/* Company is required on create; edit reads it from the loaded
		 * record (and the backend doesn't accept it in PATCH). */
		if (!isEditMode && (!Number.isFinite(cidNum) || cidNum <= 0)) {
			setError('Choose a company.');
			return;
		}
		if (!Number.isFinite(lidNum) || lidNum <= 0) {
			setError('Choose a building.');
			return;
		}
		if (!name.trim()) {
			setError('Project name is required.');
			return;
		}
		if (!Number.isFinite(hoursNum) || hoursNum < 0) {
			setError('Total hours must be a non-negative number.');
			return;
		}

		try {
			/* Common Billing + Settings payload — shared by both create
			 * and update so the values move in lockstep. */
			const billingSettingsPayload = {
				billingInfo: billingInfo.length > 0 ? billingInfo : undefined,
				billingNotes: billingNotes.trim() || undefined,
				frequency: frequency || undefined,
			};

			if (isEditMode && routeId) {
				/* PATCH ignores companyId (the backend update validator
				 * doesn't accept it — projects can't move companies);
				 * locationId, name, totalHours, etc. can all change. */
				await updateMutation.mutateAsync({
					id: routeId,
					payload: {
						locationId: lidNum,
						name: name.trim(),
						description: description.trim() || undefined,
						businessLine: businessLine || undefined,
						totalHours: hoursNum,
						status,
						startDate: startDate || undefined,
						endDate: endDate || undefined,
						notes: notes.trim() || undefined,
						...billingSettingsPayload,
					},
				});
				navigate(`/project-work-orders/${routeId}`);
				return;
			}

			const res = await createMutation.mutateAsync({
				companyId: cidNum,
				locationId: lidNum,
				name: name.trim(),
				description: description.trim() || undefined,
				businessLine: businessLine || undefined,
				totalHours: hoursNum,
				status,
				startDate: startDate || undefined,
				endDate: endDate || undefined,
				notes: notes.trim() || undefined,
				...billingSettingsPayload,
			});
			/* The create response wraps the new record. camelize so we
			 * can read .id reliably across snake_case server output. */
			const created = camelizeKeys((res.data as any)) as { id: number };
			navigate(`/project-work-orders/${created.id}`);
		} catch (err) {
			setError(err instanceof Error ? err.message : isEditMode ? 'Failed to save project' : 'Failed to create project');
		}
	};

	return (
		<PageContainer block="project-work-order-create-page" dataRef="page-project-work-order-create-page" width="form">
			<BackLink
				to={isEditMode && routeId ? `/project-work-orders/${routeId}` : '/project-work-orders'}
				label={isEditMode ? 'Back to Project' : 'Back to Projects'}
			/>

			<h1 className="page-title text-24px font-heading text-brand-heading mb-16px">
				{isEditMode
					? 'Edit Project Work Order'
					: isDuplicateMode
						? 'Duplicate Project Work Order'
						: 'New Project Work Order'}
			</h1>

			{error && (
				<Alert variant="error" className="mb-16px">{error}</Alert>
			)}

			{isLoadingEdit && (
				<div className="px-16px py-32px text-center text-14px text-brand-text-muted border border-brand-border rounded-md bg-white">
					{isDuplicateMode ? 'Loading source project…' : 'Loading project…'}
				</div>
			)}

			{!isLoadingEdit && (
			<form onSubmit={handleSubmit} className="bg-white border border-brand-border rounded-md p-20px space-y-16px">
				<div>
					<label className="block text-12px font-medium text-brand-text mb-4px">Company *</label>
					<select
						value={companyId}
						onChange={(e) => setCompanyId(e.target.value)}
						/* In edit mode the company is locked: moving a project
						   between companies would invalidate the inheritance
						   on existing child WOs, and the backend's update
						   validator doesn't accept company_id either. */
						disabled={isEditMode}
						className="w-full px-12px py-8px border border-brand-border rounded-md text-16px desktop:text-14px text-brand-text bg-white disabled:bg-gray-100 disabled:text-gray-500"
					>
						<option value="">Select a company...</option>
						{companies.map((c) => (
							<option key={c.id} value={c.id}>{c.name}</option>
						))}
					</select>
					{isEditMode && (
						<p className="mt-4px text-12px text-brand-text-muted">
							Company is locked after creation to preserve child work-order inheritance.
						</p>
					)}
				</div>

				{/* Building picker — scoped to the chosen company. Children
				    inherit this building (and its address) so the address
				    chain is consistent across all the WOs in the project.
				    Implemented with AsyncSearchableSelect so users can type
				    to filter, which matters when a company has many sites. */}
				<div>
					<label className="block text-12px font-medium text-brand-text mb-4px">Building *</label>
					<AsyncSearchableSelect
						id="projectLocation"
						value={locationId}
						/* Fall back to the label captured from the project's own
						   location relation so edit mode shows the building name
						   even before the per-company locations list arrives. */
						selectedLabel={locationOptionLabel(locations.find((l) => String(l.id) === locationId)) || prefillLocationLabel}
						onChange={(id) => setLocationId(id)}
						fetchOptions={searchLocations}
						placeholder={!companyId
							? 'Select a company first...'
							: isLoadingLocations
								? 'Loading buildings...'
								: locations.length === 0
									? 'No buildings on this company'
									: 'Search buildings...'}
						disabled={!companyId || isLoadingLocations || locations.length === 0}
					/>
					<p className="mt-4px text-12px text-brand-text-muted">
						Child work orders inherit this building and its address.
					</p>
				</div>

				<Input label="Project Name *" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Downtown HQ Renovation" />

				<div>
					<label className="block text-12px font-medium text-brand-text mb-4px">Description</label>
					<textarea
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						placeholder="Optional context for the project (scope, key stakeholders, etc.)"
						className="w-full px-12px py-8px border border-brand-border rounded-md text-16px text-brand-text min-h-[80px]"
					/>
				</div>

				{/* Business Line — project-level, the same way Billing and
				    Frequency are. A project runs on one line of business, so
				    capturing it here means every child WO inherits it instead
				    of the user re-picking (and possibly mismatching) it on
				    each one. Left optional: pre-existing projects have no
				    line, and a child can still choose its own. */}
				<div>
					<label htmlFor="projectBusinessLine" className="block text-12px font-medium text-brand-text mb-4px">Business Line</label>
					<select
						id="projectBusinessLine"
						value={businessLine}
						onChange={(e) => setBusinessLine(e.target.value)}
						className="w-full px-12px py-8px border border-brand-border rounded-md text-16px desktop:text-14px text-brand-text bg-white"
					>
						<option value="">— Select business line —</option>
						{businessLines.filter((bl) => bl.is_active).map((bl) => (
							<option key={bl.id} value={bl.slug}>{bl.label}</option>
						))}
					</select>
					<p className="mt-4px text-12px text-brand-text-muted">
						Child work orders inherit this business line.
					</p>
				</div>

				<div className="grid grid-cols-1 tablet:grid-cols-2 gap-16px">
					<Input label="Total Project Hours *" type="number" min={0} step={0.25} value={totalHours} onChange={(e) => setTotalHours(e.target.value)} placeholder="e.g., 100" />
					<div>
						<label className="block text-12px font-medium text-brand-text mb-4px">Status</label>
						<select
							value={status}
							onChange={(e) => setStatus(e.target.value as ProjectWorkOrderStatus)}
							className="w-full px-12px py-8px border border-brand-border rounded-md text-16px desktop:text-14px text-brand-text bg-white"
						>
							<option value="draft">Draft</option>
							<option value="active">Active</option>
							<option value="completed">Completed</option>
							<option value="cancelled">Cancelled</option>
						</select>
					</div>
				</div>

				<div className="grid grid-cols-1 tablet:grid-cols-2 gap-16px">
					<Input label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
					<Input label="End Date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
				</div>

				<div>
					<label className="block text-12px font-medium text-brand-text mb-4px">Notes</label>
					<textarea
						value={notes}
						onChange={(e) => setNotes(e.target.value)}
						placeholder="Internal notes (not shown to field users)"
						className="w-full px-12px py-8px border border-brand-border rounded-md text-16px text-brand-text min-h-[60px]"
					/>
				</div>

				{/* --- Billing --- moved up from the per-WO form so the
				    classification + notes apply to every child WO in the
				    project. Children no longer surface this section on
				    their own create form. */}
				<div className="border-t border-brand-border pt-16px">
					<h2 className="text-14px font-semibold text-brand-text mb-8px">Billing</h2>
					<div>
						<label className="block text-12px font-medium text-brand-text mb-4px">Billing Classification</label>
						<div className="flex flex-wrap gap-8px">
							{BILLING_INFO_OPTIONS.map((option) => {
								const checked = billingInfo.includes(option);
								return (
									<button
										key={option}
										type="button"
										onClick={() => {
											setBillingInfo((prev) => (
												prev.includes(option)
													? prev.filter((o) => o !== option)
													: [...prev, option]
											));
										}}
										className={[
											/* min-h-44px on touch: these tag chips are 32px from
											   padding alone and sit in a tightly packed grid. */
											'inline-flex items-center px-12px py-6px min-h-44px desktop:min-h-0 rounded-full text-12px font-medium border transition-colors',
											checked
												? 'bg-brand-primary text-white border-brand-primary'
												: 'bg-white text-brand-text border-brand-border hover:border-brand-primary'
										].join(' ')}
									>
										{option}
									</button>
								);
							})}
						</div>
					</div>
					<div className="mt-12px">
						<label className="block text-12px font-medium text-brand-text mb-4px">Billing Notes</label>
						<textarea
							value={billingNotes}
							onChange={(e) => setBillingNotes(e.target.value)}
							placeholder="Free-form billing notes for the whole project"
							className="w-full px-12px py-8px border border-brand-border rounded-md text-16px text-brand-text min-h-[60px]"
						/>
					</div>
				</div>

				{/* --- Settings --- the only project-level setting today is
				    the recurring frequency. Photo Required / Signature
				    Required / Checklist stay on each child WO since those
				    can legitimately vary across WOs. */}
				<div className="border-t border-brand-border pt-16px">
					<h2 className="text-14px font-semibold text-brand-text mb-8px">Settings</h2>
					<div>
						<label className="block text-12px font-medium text-brand-text mb-4px">Frequency</label>
						<select
							value={frequency}
							onChange={(e) => setFrequency(e.target.value)}
							className="w-full px-12px py-8px border border-brand-border rounded-md text-16px desktop:text-14px text-brand-text bg-white"
						>
							<option value="">— Select frequency —</option>
							{FREQUENCY_OPTIONS.map((f) => (
								<option key={f} value={f}>{f}</option>
							))}
						</select>
					</div>
				</div>

				<div className="flex flex-wrap items-center justify-end gap-8px pt-8px border-t border-brand-border">
					<Button
						type="button"
						variant="ghost"
						onClick={() => navigate(isEditMode && routeId ? `/project-work-orders/${routeId}` : '/project-work-orders')}
						disabled={isSaving}
					>
						Cancel
					</Button>
					<Button type="submit" variant="primary" disabled={isSaving}>
						{isSaving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Create Project')}
					</Button>
				</div>
			</form>
			)}

			{/* Helper text is create-only — on edit the user already knows
			    where they are and what attaching child WOs means. */}
			{!isEditMode && !isLoadingEdit && (
				<p className="text-12px text-brand-text-muted mt-16px">
					After creation you'll be taken to the project page where you can attach work orders and set their hour allocations.
				</p>
			)}
		</PageContainer>
	);
};

export default ProjectWorkOrderCreatePage;
